// Dashboard event stream — the shared event contract for every "channel" the
// classic dashboard shows (price changes, counts, orders, invoices, Countertop
// messages, upcoming schedules), normalized into a single typed feed:
//
//   {
//     id:    string           — stable dedupe key ("kind:entity[:qualifier]")
//     kind:  string           — 'price_change' | 'count_created' | 'count_started'
//                             | 'count_completed' | 'count_upcoming'
//                             | 'order_sent' | 'order_upcoming'
//                             | 'invoice' | 'message'
//     title: string           — one-line headline (push-notification ready)
//     body:  string|null      — optional detail line
//     href:  string|null      — deep link into the app
//     ts:    number           — epoch ms (a future date for *_upcoming kinds)
//     future: boolean         — true for scheduled/upcoming entries (sorted
//                               soonest-first ahead of the newest-first past)
//     live:  boolean          — true when delivered via realtime, not a snapshot
//     messageId / dismissable — set for Countertop messages ("✓ Got it" flow)
//   }
//
// The same shape is meant to be reused server-side later (DB webhook →
// send-push-notifications edge function) so the feed, badges, and push all
// speak one event language as the classic dashboard is deprecated in favor of
// the command center. Everything here is pure data-in/data-out for testing.

import { isSessionCompleted, isSessionInProgress } from './commandCenterStatus';
import { buildUpcomingVendorEvents, startOfDay } from './vendorSchedules';

export const MAX_FEED_EVENTS = 100;

export const localTodayIso = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const tsOf = (value) => {
  const ts = Date.parse(value || '');
  return Number.isFinite(ts) ? ts : null;
};

const money = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : null;
};

const shortDate = (isoDate) => {
  if (!isoDate) return '';
  const dt = new Date(`${String(isoDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

/** 'today' / 'tomorrow' / 'in N days' for a timestamp, by calendar day. */
export const relDayLabel = (ts, now = Date.now()) => {
  const days = Math.round(
    (startOfDay(new Date(ts)).getTime() - startOfDay(new Date(now)).getTime()) / DAY_MS,
  );
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
};

// ── Row → event mappers (also used for realtime payloads) ──────────────────

/** price_history row (change_type 'updated') → price_change event. */
export function priceChangeEvent(row, { live = false } = {}) {
  if (!row) return null;
  const ts = tsOf(row.changed_at);
  if (!ts) return null;
  const name = row.new_name || row.old_name || '(unnamed)';
  const vendor = row.invoice_vendor || '';
  const oldP = money(row.old_case_price);
  const newP = money(row.new_case_price);
  const range = oldP && newP ? `${oldP} → ${newP}` : (newP ? `${newP} — new price` : '');
  return {
    id: `price_change:${row.id}`,
    kind: 'price_change',
    title: `Price updated: ${name}`,
    body: [vendor, range].filter(Boolean).join(' · ') || null,
    href: '/items.html',
    ts,
    future: false,
    live,
  };
}

/**
 * count_sessions row → one event per lifecycle fact it carries (created,
 * started, completed, scheduled). Safe to call for both snapshot rows and
 * realtime INSERT/UPDATE payloads — feed dedupe by id keeps each fact once.
 */
export function sessionEvents(session, { todayIso = localTodayIso(), live = false, now = Date.now() } = {}) {
  if (!session || session.id == null) return [];
  const name = session.name || 'Count';
  const events = [];
  const createdTs = tsOf(session.created_at);
  const startedTs = tsOf(session.started_at);
  const completedTs = tsOf(session.completed_at);

  if (createdTs) {
    events.push({
      id: `count_created:${session.id}`,
      kind: 'count_created',
      title: `Count created: ${name}`,
      body: session.session_date ? `Scheduled ${shortDate(session.session_date)}` : null,
      href: '/count.html?view=overview',
      ts: createdTs,
      future: false,
      live,
    });
  }
  if (startedTs && !isSessionCompleted(session)) {
    events.push({
      id: `count_started:${session.id}`,
      kind: 'count_started',
      title: `Count started: ${name}`,
      body: 'In progress',
      href: '/count.html?view=overview',
      ts: startedTs,
      future: false,
      live,
    });
  }
  if (isSessionCompleted(session) && (completedTs || tsOf(session.updated_at))) {
    events.push({
      id: `count_completed:${session.id}`,
      kind: 'count_completed',
      title: `Count completed: ${name}`,
      body: null,
      href: '/count.html?view=overview',
      ts: completedTs || tsOf(session.updated_at),
      future: false,
      live,
    });
  }
  if (session.session_date && session.session_date >= todayIso && !isSessionCompleted(session) && !isSessionInProgress(session)) {
    const ts = tsOf(`${session.session_date}T00:00:00`) || now;
    events.push({
      id: `count_upcoming:${session.id}:${session.session_date}`,
      kind: 'count_upcoming',
      title: `${name} — ${relDayLabel(ts, now)}`,
      body: 'Scheduled count',
      href: '/count.html?view=overview',
      ts,
      future: true,
      live,
    });
  }
  return events;
}

/** purchase_orders row with sent_at set → order_sent event. */
export function orderSentEvent(po, { live = false } = {}) {
  if (!po) return null;
  const ts = tsOf(po.sent_at);
  if (!ts) return null;
  return {
    id: `order_sent:${po.id}:${po.sent_at}`,
    kind: 'order_sent',
    title: `Order sent to ${po.vendor_name_snapshot || 'vendor'}`,
    body: null,
    href: '/ordering.html',
    ts,
    future: false,
    live,
  };
}

/** Vendor ordering schedules → order_upcoming events for the week ahead. */
export function orderUpcomingEvents(vendors, { live = false, now = Date.now() } = {}) {
  return buildUpcomingVendorEvents(vendors || [], 'order', 7).map((event) => ({
    id: `order_upcoming:${event.key}`,
    kind: 'order_upcoming',
    title: `${event.vendor} order ${relDayLabel(event.date.getTime(), now)}`,
    body: event.cutoffTime ? `Cutoff ${event.cutoffTime}` : (event.notes || null),
    href: '/ordering.html',
    ts: event.date.getTime(),
    future: true,
    live,
  }));
}

/** store-invoice-processor inbox row → invoice event. */
export function invoiceEvent(inv, { live = false } = {}) {
  if (!inv) return null;
  const ts = tsOf(inv.received_at || inv.processed_at);
  if (!ts) return null;
  return {
    id: `invoice:${inv.id || `${inv.vendor_name || inv.from_email || 'vendor'}-${ts}`}`,
    kind: 'invoice',
    title: `Invoice from ${inv.vendor_name || inv.from_email || 'vendor'}`,
    body: null,
    href: '/invoice-inbox.html',
    ts,
    future: false,
    live,
  };
}

/** Unread Countertop-app message → dismissable message event. */
export function messageEvent(msg, { live = false } = {}) {
  if (!msg || msg.read_at) return null;
  const ts = tsOf(msg.created_at);
  if (!ts) return null;
  return {
    id: `message:${msg.id}`,
    kind: 'message',
    title: msg.title || 'Message from Countertop-app',
    body: msg.body || null,
    href: null,
    ts,
    future: false,
    live,
    messageId: msg.id,
    dismissable: true,
  };
}

// ── Feed assembly ───────────────────────────────────────────────────────────

/**
 * Feed ordering: scheduled/future entries first (soonest → latest), then the
 * live/past stream (newest → oldest), deduped by id and capped.
 */
export function sortFeed(events, { limit = MAX_FEED_EVENTS } = {}) {
  const map = new Map();
  (events || []).forEach((event) => {
    if (event && event.id && Number.isFinite(event.ts)) map.set(event.id, event);
  });
  const all = [...map.values()];
  const future = all.filter((e) => e.future).sort((a, b) => a.ts - b.ts);
  const past = all.filter((e) => !e.future).sort((a, b) => b.ts - a.ts);
  return [...future, ...past].slice(0, limit);
}

/**
 * Build the full feed from one command-center snapshot (the same data object
 * loadCommandCenterData returns). Unread messages only — read ones stay out,
 * matching the classic dashboard's "Got it" behavior.
 */
export function buildDashboardFeed(data = {}, { todayIso = localTodayIso(), now = Date.now(), limit } = {}) {
  const events = [];
  (data.priceRows || []).forEach((row) => events.push(priceChangeEvent(row, { live: false })));
  (data.sessions || []).forEach((s) => events.push(...sessionEvents(s, { todayIso, now, live: false })));
  (data.sentPOs || []).forEach((po) => events.push(orderSentEvent(po, { live: false })));
  events.push(...orderUpcomingEvents(data.vendors || [], { now, live: false }));
  (data.invoiceHistory || []).forEach((inv) => events.push(invoiceEvent(inv, { live: false })));
  (data.messages || []).forEach((msg) => events.push(messageEvent(msg, { live: false })));
  return sortFeed(events.filter(Boolean), { limit });
}
